import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Image as ImgIcon, Save, Scan, History, ChevronLeft, X } from "lucide-react";
import { inventoryApi } from "../lib/shop-api";
import { money, dateShort } from "../lib/api";
import { BarcodeScanner } from "../components/BarcodeScanner";

const EMPTY = {
  name: "", description: "", category: "", brand: "", price: 0, compare_at_price: null,
  barcode: "", sku: "", images: [], variants: [], quantity_simple: 0,
  tags: [], featured: false, new_arrival: false, on_sale: false, is_active: true, low_stock_threshold: 5,
};

const sizePresets = {
  ropa: ["XS", "S", "M", "L", "XL", "2XL", "3XL"],
  botas_us: ["6","6.5","7","7.5","8","8.5","9","9.5","10","10.5","11","11.5","12","13"],
  sombrero: ["6-3/4","6-7/8","7","7-1/8","7-1/4","7-3/8","7-1/2","7-5/8","7-3/4"],
  jeans: ["28","30","32","33","34","36","38","40","42","44"],
};

export default function AdminProductForm() {
  const { id } = useParams();
  const isNew = !id;
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [p, setP] = useState({ ...EMPTY, barcode: sp.get("barcode") || "" });
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [scanField, setScanField] = useState(null); // "product" | variant index number | null

  useEffect(() => { inventoryApi.categories().then(setCats).catch(() => {}); }, []);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    Promise.all([inventoryApi.getProduct(id), inventoryApi.history(id)])
      .then(([prod, h]) => { setP(prod); setHistory(h); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id, isNew]);

  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  const addImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Max 2MB por imagen"); return; }
    const reader = new FileReader();
    reader.onload = () => set("images", [...(p.images || []), reader.result]);
    reader.readAsDataURL(file);
    e.target.value = "";
  };
  const removeImage = (i) => set("images", p.images.filter((_, idx) => idx !== i));

  const addVariant = (sizes = [""]) => {
    const newOnes = sizes.map((s) => ({ size: s, color: "", quantity: 0, barcode: "", sku: "", id: Math.random().toString(36).slice(2, 10) }));
    set("variants", [...(p.variants || []), ...newOnes]);
  };
  const applyPreset = (key) => addVariant(sizePresets[key]);
  const updateVariant = (idx, patch) =>
    set("variants", p.variants.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
  const removeVariant = (idx) =>
    set("variants", p.variants.filter((_, i) => i !== idx));

  const save = async () => {
    if (!p.name || !p.category || !(p.price > 0)) { toast.error("Nombre, categoría y precio son requeridos"); return; }
    setSaving(true);
    try {
      const payload = { ...p, price: Number(p.price), compare_at_price: p.compare_at_price ? Number(p.compare_at_price) : null, low_stock_threshold: Number(p.low_stock_threshold) || 5, quantity_simple: Number(p.quantity_simple) || 0, variants: (p.variants || []).map(v => ({ ...v, quantity: Number(v.quantity) || 0 })) };
      if (isNew) {
        const created = await inventoryApi.createProduct(payload);
        toast.success("Producto creado");
        nav(`/admin/products/${created.id}`);
      } else {
        const updated = await inventoryApi.patchProduct(id, payload);
        setP(updated);
        toast.success("Guardado");
      }
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm("¿Eliminar este producto? No se puede deshacer.")) return;
    try {
      await inventoryApi.deleteProduct(id);
      toast.success("Eliminado");
      nav("/admin/products");
    } catch (e) { toast.error(e.message); }
  };

  const quickAdjust = async (variantId, delta) => {
    try {
      const updated = await inventoryApi.adjustStock(id, { variant_id: variantId, delta, reason: delta > 0 ? "entrada rápida" : "salida rápida" });
      setP(updated);
      const h = await inventoryApi.history(id);
      setHistory(h);
    } catch (e) { toast.error(e.message); }
  };

  const onScanned = (code) => {
    if (scanField === "product") set("barcode", code);
    else if (typeof scanField === "number") updateVariant(scanField, { barcode: code });
    setScanField(null);
    toast.success("Código capturado");
  };

  if (loading) return <p className="text-[#F5F0E6]/60">Cargando…</p>;

  return (
    <div className="max-w-5xl">
      <Link to="/admin/products" className="inline-flex items-center gap-1 text-sm text-[#F5F0E6]/60 hover:text-[#C89B3C] mb-4">
        <ChevronLeft size={16} /> Productos
      </Link>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-4xl text-[#F5F0E6]" style={{ fontFamily: "'Playfair Display'", fontWeight: 700 }}>
          {isNew ? "Nuevo producto" : p.name}
        </h1>
        <div className="flex gap-2">
          {!isNew && <button onClick={del} className="text-xs text-red-400 hover:text-red-300 px-3 py-2" data-testid="product-delete">Eliminar</button>}
          <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#C89B3C] hover:bg-[#E6BF5F] text-[#0A0908] font-bold uppercase tracking-wider px-5 py-2.5 rounded-full disabled:opacity-60" data-testid="product-save">
            <Save size={16} /> {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-6">
          {/* Basics */}
          <section className="bg-[#120F0D] border border-white/10 rounded-xl p-5 space-y-3">
            <h2 className="text-lg text-[#F5F0E6]" style={{ fontFamily: "'Playfair Display'" }}>Información básica</h2>
            <input value={p.name} onChange={(e) => set("name", e.target.value)} placeholder="Nombre" className="w-full bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm text-[#F5F0E6]" data-testid="product-input-name" />
            <textarea value={p.description} onChange={(e) => set("description", e.target.value)} placeholder="Descripción" rows={4} className="w-full bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm text-[#F5F0E6]" data-testid="product-input-desc" />
            <div className="grid grid-cols-2 gap-3">
              <select value={p.category} onChange={(e) => set("category", e.target.value)} className="bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm text-[#F5F0E6]" data-testid="product-input-category">
                <option value="">Elige categoría</option>
                {cats.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input value={p.brand} onChange={(e) => set("brand", e.target.value)} placeholder="Marca" className="bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm text-[#F5F0E6]" data-testid="product-input-brand" />
            </div>
          </section>

          {/* Pricing + codes */}
          <section className="bg-[#120F0D] border border-white/10 rounded-xl p-5 space-y-3">
            <h2 className="text-lg text-[#F5F0E6]" style={{ fontFamily: "'Playfair Display'" }}>Precio y códigos</h2>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-[#F5F0E6]/60">Precio</label><input type="number" step="0.01" value={p.price} onChange={(e) => set("price", e.target.value)} className="w-full bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm text-[#F5F0E6]" data-testid="product-input-price" /></div>
              <div><label className="text-xs text-[#F5F0E6]/60">Precio tachado (opcional)</label><input type="number" step="0.01" value={p.compare_at_price || ""} onChange={(e) => set("compare_at_price", e.target.value || null)} className="w-full bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm text-[#F5F0E6]" /></div>
              <div><label className="text-xs text-[#F5F0E6]/60">SKU</label><input value={p.sku || ""} onChange={(e) => set("sku", e.target.value)} className="w-full bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm text-[#F5F0E6] font-mono" data-testid="product-input-sku" /></div>
              <div>
                <label className="text-xs text-[#F5F0E6]/60">Barcode (UPC/EAN)</label>
                <div className="flex gap-1">
                  <input value={p.barcode || ""} onChange={(e) => set("barcode", e.target.value)} className="flex-1 bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm text-[#F5F0E6] font-mono" data-testid="product-input-barcode" />
                  <button type="button" onClick={() => setScanField("product")} className="bg-[#C89B3C] text-[#0A0908] px-2 rounded hover:bg-[#E6BF5F]" title="Escanear" data-testid="product-scan-barcode"><Scan size={16} /></button>
                </div>
              </div>
              <div><label className="text-xs text-[#F5F0E6]/60">Low stock umbral</label><input type="number" value={p.low_stock_threshold} onChange={(e) => set("low_stock_threshold", e.target.value)} className="w-full bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm text-[#F5F0E6]" /></div>
            </div>
          </section>

          {/* Images */}
          <section className="bg-[#120F0D] border border-white/10 rounded-xl p-5">
            <h2 className="text-lg text-[#F5F0E6] mb-3" style={{ fontFamily: "'Playfair Display'" }}>Fotos</h2>
            <div className="flex flex-wrap gap-3">
              {(p.images || []).map((im, i) => (
                <div key={i} className="relative w-24 h-24 rounded overflow-hidden border border-white/10">
                  <img src={im} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-black/80 rounded-full p-1 text-white hover:bg-red-500"><X size={12} /></button>
                </div>
              ))}
              <label className="w-24 h-24 border-2 border-dashed border-white/15 rounded flex flex-col items-center justify-center text-[#F5F0E6]/50 text-xs cursor-pointer hover:border-[#C89B3C] hover:text-[#C89B3C]" data-testid="product-upload-image">
                <ImgIcon size={20} /><span>Agregar</span>
                <input type="file" accept="image/*" onChange={addImage} className="hidden" />
              </label>
            </div>
          </section>

          {/* Variants */}
          <section className="bg-[#120F0D] border border-white/10 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg text-[#F5F0E6]" style={{ fontFamily: "'Playfair Display'" }}>Variantes (tallas / colores)</h2>
              <div className="flex gap-1 flex-wrap">
                <button onClick={() => applyPreset("ropa")} className="text-xs text-[#C89B3C] hover:text-[#E6BF5F] px-2 py-1 border border-[#C89B3C]/40 rounded" data-testid="preset-ropa">Preset Ropa</button>
                <button onClick={() => applyPreset("botas_us")} className="text-xs text-[#C89B3C] hover:text-[#E6BF5F] px-2 py-1 border border-[#C89B3C]/40 rounded" data-testid="preset-botas">Preset Botas</button>
                <button onClick={() => applyPreset("sombrero")} className="text-xs text-[#C89B3C] hover:text-[#E6BF5F] px-2 py-1 border border-[#C89B3C]/40 rounded" data-testid="preset-sombrero">Preset Sombrero</button>
                <button onClick={() => addVariant([""])} className="text-xs flex items-center gap-1 bg-white/5 border border-white/15 text-[#F5F0E6] px-2 py-1 rounded hover:border-[#C89B3C]" data-testid="add-variant"><Plus size={12} /> Agregar</button>
              </div>
            </div>
            {(!p.variants || p.variants.length === 0) ? (
              <div className="space-y-3">
                <p className="text-sm text-[#F5F0E6]/60">Sin variantes. Si el producto es talla única, usa el stock simple abajo.</p>
                <div className="flex items-center gap-3">
                  <label className="text-xs text-[#F5F0E6]/60">Stock simple</label>
                  <input type="number" value={p.quantity_simple} onChange={(e) => set("quantity_simple", e.target.value)} className="w-24 bg-[#0A0908] border border-white/15 rounded px-3 py-2 text-sm text-[#F5F0E6]" data-testid="product-input-qty-simple" />
                </div>
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="product-variants-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-widest text-[#F5F0E6]/50">
                    <th className="py-2">Talla</th>
                    <th>Color</th>
                    <th>SKU</th>
                    <th>Barcode</th>
                    <th>Qty</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {p.variants.map((v, i) => (
                    <tr key={v.id || i} className="border-t border-white/5" data-testid={`variant-row-${i}`}>
                      <td className="py-2 pr-2"><input value={v.size || ""} onChange={(e) => updateVariant(i, { size: e.target.value })} className="w-20 bg-[#0A0908] border border-white/15 rounded px-2 py-1 text-sm text-[#F5F0E6]" data-testid={`variant-size-${i}`} /></td>
                      <td className="pr-2"><input value={v.color || ""} onChange={(e) => updateVariant(i, { color: e.target.value })} className="w-24 bg-[#0A0908] border border-white/15 rounded px-2 py-1 text-sm text-[#F5F0E6]" data-testid={`variant-color-${i}`} /></td>
                      <td className="pr-2"><input value={v.sku || ""} onChange={(e) => updateVariant(i, { sku: e.target.value })} className="w-24 bg-[#0A0908] border border-white/15 rounded px-2 py-1 text-sm text-[#F5F0E6] font-mono" /></td>
                      <td className="pr-2">
                        <div className="flex gap-1">
                          <input value={v.barcode || ""} onChange={(e) => updateVariant(i, { barcode: e.target.value })} className="w-28 bg-[#0A0908] border border-white/15 rounded px-2 py-1 text-sm text-[#F5F0E6] font-mono" data-testid={`variant-barcode-${i}`} />
                          <button type="button" onClick={() => setScanField(i)} className="text-[#C89B3C] px-1" title="Escanear"><Scan size={14} /></button>
                        </div>
                      </td>
                      <td className="pr-2 flex items-center gap-1 py-2">
                        {!isNew && (
                          <>
                            <button onClick={() => quickAdjust(v.id, -1)} className="text-red-400 hover:text-red-300 text-xs">-1</button>
                            <button onClick={() => quickAdjust(v.id, 1)} className="text-green-400 hover:text-green-300 text-xs">+1</button>
                          </>
                        )}
                        <input type="number" value={v.quantity} onChange={(e) => updateVariant(i, { quantity: e.target.value })} className="w-16 bg-[#0A0908] border border-white/15 rounded px-2 py-1 text-sm text-[#F5F0E6]" data-testid={`variant-qty-${i}`} />
                      </td>
                      <td><button onClick={() => removeVariant(i)} className="text-[#F5F0E6]/40 hover:text-red-400 p-1"><Trash2 size={14} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </div>

        <aside className="space-y-6">
          <section className="bg-[#120F0D] border border-white/10 rounded-xl p-5 space-y-3">
            <h2 className="text-lg text-[#F5F0E6]" style={{ fontFamily: "'Playfair Display'" }}>Visibilidad</h2>
            {["is_active","featured","new_arrival","on_sale"].map((k) => (
              <label key={k} className="flex items-center gap-2 text-sm text-[#F5F0E6]/80">
                <input type="checkbox" checked={!!p[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`flag-${k}`} />
                {k === "is_active" ? "Activo (visible en tienda)" : k === "featured" ? "Destacado" : k === "new_arrival" ? "Nuevo ingreso" : "En oferta"}
              </label>
            ))}
            {!isNew && (
              <div className="pt-3 border-t border-white/10">
                <p className="text-xs text-[#F5F0E6]/50">Total stock</p>
                <p className="text-3xl font-bold text-[#C89B3C]" data-testid="product-total-stock">{p.total_stock || 0}</p>
                {p.updated_at && <p className="text-xs text-[#F5F0E6]/40 mt-2">Actualizado {dateShort(p.updated_at)}</p>}
              </div>
            )}
          </section>

          {!isNew && (
            <section className="bg-[#120F0D] border border-white/10 rounded-xl p-5">
              <h2 className="text-lg text-[#F5F0E6] flex items-center gap-2 mb-3" style={{ fontFamily: "'Playfair Display'" }}>
                <History size={18} /> Historial
              </h2>
              {history.length === 0 ? (
                <p className="text-xs text-[#F5F0E6]/50">Sin movimientos aún.</p>
              ) : (
                <ul className="space-y-2 max-h-96 overflow-y-auto text-xs" data-testid="product-history-list">
                  {history.map((e) => (
                    <li key={e.id} className="border-l-2 border-[#C89B3C]/40 pl-3 py-1">
                      <p className="text-[#F5F0E6]">{e.previous_quantity} → <span className="text-[#C89B3C] font-bold">{e.new_quantity}</span> <span className={e.delta > 0 ? "text-green-400" : "text-red-400"}>({e.delta > 0 ? "+" : ""}{e.delta})</span></p>
                      <p className="text-[#F5F0E6]/60">{e.size ? `${e.size}${e.color ? " · " + e.color : ""} · ` : ""}{e.reason}</p>
                      <p className="text-[#F5F0E6]/40 text-[10px]">{dateShort(e.timestamp)} · {e.user}</p>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </aside>
      </div>

      {scanField !== null && (
        <BarcodeScanner
          title={scanField === "product" ? "Barcode del producto" : "Barcode de variante"}
          onDetected={onScanned}
          onClose={() => setScanField(null)}
        />
      )}
    </div>
  );
}
